import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, RefreshCw, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { API } from '../config';
import { devLog } from '../utils/devLog';

const ENTITY_OPTIONS = [
  { v: '', label: 'All entities' },
  { v: 'plant', label: 'Plant' },
  { v: 'shift', label: 'Shift Data' },
  { v: 'rate', label: 'Rates' },
  { v: 'rate_history', label: 'Rate History' },
  { v: 'user', label: 'User' },
  { v: 'co2_loss', label: 'CO₂ Loss' },
  { v: 'edit_request', label: 'Edit Request' },
];

const ACTION_OPTIONS = [
  { v: '', label: 'All actions' },
  { v: 'create', label: 'Create' },
  { v: 'update', label: 'Update' },
  { v: 'delete', label: 'Delete' },
];

const ACTION_BADGE = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
};

const fmtTs = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) { return iso; }
};

const ChangesPanel = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return <p className="text-xs text-gray-500 italic">No field-level diff (create or delete)</p>;
  }
  return (
    <div className="text-xs overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-100 text-gray-700">
            <th className="text-left px-2 py-1">Field</th>
            <th className="text-left px-2 py-1">Before</th>
            <th className="text-left px-2 py-1">After</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(changes).map(([k, v]) => (
            <tr key={k} className="border-t border-gray-200">
              <td className="px-2 py-1 font-medium text-gray-800">{k}</td>
              <td className="px-2 py-1 text-red-700 font-mono">{JSON.stringify(v.before)}</td>
              <td className="px-2 py-1 text-green-700 font-mono">{JSON.stringify(v.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AuditLogRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`audit-row-${entry.id}`}>
        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtTs(entry.timestamp)}</td>
        <td className="px-3 py-2 font-medium text-gray-900">{entry.username}<span className="text-xs text-gray-500 block">{entry.role}</span></td>
        <td className="px-3 py-2">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_BADGE[entry.action] || 'bg-gray-100'}`}>{entry.action}</span>
        </td>
        <td className="px-3 py-2 text-sm text-gray-700">{entry.entity_type}</td>
        <td className="px-3 py-2 text-sm text-gray-700">{entry.entity_label}</td>
        <td className="px-3 py-2 text-sm text-gray-600 max-w-md">{entry.summary}</td>
        <td className="px-3 py-2">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
      </tr>
      {open && (
        <tr className="bg-gray-50">
          <td colSpan={7} className="px-6 py-3" data-testid={`audit-detail-${entry.id}`}>
            <ChangesPanel changes={entry.changes} />
          </td>
        </tr>
      )}
    </>
  );
};

const AuditLogView = ({ plants, token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ entity_type: '', action: '', username: '', plant_id: '', date_from: '', date_to: '' });
  const [limit] = useState(100);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/audit-logs`, { headers: authHeaders, params });
      setEntries(res.data || []);
    } catch (err) {
      devLog.error('Failed to load audit logs:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, token, limit]);

  useEffect(() => { load(); }, [load]);

  const setF = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-[#156ACA] flex-shrink-0" /> Audit Log
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Every create / update / delete — tap a row to see the field-level diff.</p>
          </div>
          <button onClick={load} className="bg-[#156ACA] hover:bg-[#0F5299] text-white font-semibold py-2 px-4 min-h-[40px] rounded-lg flex items-center" data-testid="audit-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-6 flex items-center text-xs text-gray-600 mb-1">
            <Filter className="h-3 w-3 mr-1" /> Filters
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Entity</label>
            <select value={filters.entity_type} onChange={(e) => setF('entity_type', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="audit-filter-entity">
              {ENTITY_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Action</label>
            <select value={filters.action} onChange={(e) => setF('action', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="audit-filter-action">
              {ACTION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Username</label>
            <input type="text" value={filters.username} onChange={(e) => setF('username', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="exact match" data-testid="audit-filter-username" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Plant</label>
            <select value={filters.plant_id} onChange={(e) => setF('plant_id', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="audit-filter-plant">
              <option value="">All plants</option>
              {plants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">From</label>
            <input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="audit-filter-from" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">To</label>
            <input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-gray-300 rounded text-sm" data-testid="audit-filter-to" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading && entries.length === 0 ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="audit-empty">No audit log entries match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-left px-3 py-2">Entity</th>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">Summary</th>
                  <th className="text-left px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => <AuditLogRow key={e.id} entry={e} />)}
              </tbody>
            </table>
            <div className="text-xs text-gray-500 px-3 py-2 border-t border-gray-200">Showing latest {entries.length} entries (max {limit})</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogView;
